import {
  createInviteResponseSchema,
  inviteStatusSchema,
  loginResponseSchema,
  meResponseSchema,
  registerResponseSchema,
  updateProfileResponseSchema,
  type CreateInviteResponse,
  type InviteStatus,
  type LoginRequest,
  type MemberColor,
  type PublicUser,
  type UpdateProfileRequest,
} from '@awwyeah/shared';

/** Thrown when a request completes but the server reports a boundary/domain error. */
export class AuthApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
  ) {
    super(code);
    this.name = 'AuthApiError';
  }
}

async function postJson(path: string, body: unknown): Promise<Response> {
  return fetch(path, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
}

async function patchJson(path: string, body: unknown): Promise<Response> {
  return fetch(path, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
}

async function readErrorCode(res: Response): Promise<string> {
  try {
    const data = (await res.json()) as { error?: string };
    return data.error ?? `http_${res.status}`;
  } catch {
    return `http_${res.status}`;
  }
}

/** Zod-validates the successful-response shape; throws `AuthApiError` on any non-2xx. */
async function expectOk<T>(res: Response, parse: (data: unknown) => T): Promise<T> {
  if (!res.ok) {
    throw new AuthApiError(res.status, await readErrorCode(res));
  }
  return parse(await res.json());
}

export async function apiLogin(input: LoginRequest): Promise<PublicUser> {
  const res = await postJson('/api/auth/login', input);
  const data = await expectOk(res, (d) => loginResponseSchema.parse(d));
  return data.user;
}

export async function apiLogout(): Promise<void> {
  await postJson('/api/auth/logout', {});
}

/** Returns `null` when there is no session (401), rather than throwing. */
export async function apiMe(): Promise<PublicUser | null> {
  const res = await fetch('/api/auth/me', { credentials: 'include' });
  if (res.status === 401) return null;
  const data = await expectOk(res, (d) => meResponseSchema.parse(d));
  return data.user;
}

export async function apiCreateInvite(): Promise<CreateInviteResponse> {
  const res = await postJson('/api/auth/invites', {});
  return expectOk(res, (d) => createInviteResponseSchema.parse(d));
}

export async function apiGetInvite(token: string): Promise<InviteStatus> {
  const res = await fetch(`/api/auth/invites/${encodeURIComponent(token)}`);
  return expectOk(res, (d) => inviteStatusSchema.parse(d));
}

export interface RegisterInput {
  inviteToken: string;
  name: string;
  email: string;
  password: string;
  color: MemberColor;
  avatarEmoji: string;
}

export async function apiRegister(input: RegisterInput): Promise<PublicUser> {
  const res = await postJson('/api/auth/register', input);
  const data = await expectOk(res, (d) => registerResponseSchema.parse(d));
  return data.user;
}

/** `PATCH /api/auth/me` — rename/recolor/re-avatar yourself after registration. */
export async function apiUpdateProfile(patch: UpdateProfileRequest): Promise<PublicUser> {
  const res = await patchJson('/api/auth/me', patch);
  const data = await expectOk(res, (d) => updateProfileResponseSchema.parse(d));
  return data.user;
}
